import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import Player from '@vimeo/player';

const VimeoPlayer = forwardRef(({
  url,
  playing,
  controls,
  pip,
  loop,
  playbackRate,
  volume,
  muted,
  playerWidth,
  playerHeight,
  onReady,
  onPlay,
  onPause,
  onEnded,
  onError,
  onProgress,
  onDuration,
}, ref) => {
  const internalRef = useRef(null); // Internal ref for the player DOM
  const [player, setPlayer] = useState(null);
  const [lastPos, setLastPos] = useState(null);

  useImperativeHandle(ref, () => ({
    seekTo: (time) => {
      if (player) {
        player.setCurrentTime(time); // Vimeo's method to seek
        setLastPos(time);
      }
    },
    getPlayer: () => player,
    play: () => player?.play(),
    pause: () => player?.pause(),
    setVolume: (volume) => player?.setVolume(volume),
  }));
  

  useEffect(() => {
    if (!internalRef.current) return;

    // Initialize the Vimeo player
    const vimeoPlayer = new Player(internalRef.current, {
      url: url,
      autoplay: playing,
      controls: controls,
      loop: loop,
      pip: pip,
      playbackRate: playbackRate,
      volume: volume,
      muted: muted,
      width: playerWidth,  // Using playerWidth passed as prop
      height: playerHeight,  // Using playerHeight passed as prop
    });

    // Set player event listeners
    vimeoPlayer.on('loaded', () => {
      if (onReady) onReady();

      // Seek to the last position (if available) when the player is ready
      if (lastPos !== null) {
        vimeoPlayer.setCurrentTime(lastPos).catch((error) => {
          console.error('Error seeking to last position:', error);
        });
      }
    });
    vimeoPlayer.on('play', () => {
      if (onPlay) onPlay();
    });
    vimeoPlayer.on('pause', () => {
      if (onPause) onPause();
    });
    vimeoPlayer.on('ended', () => {
      if (onEnded) onEnded();
    });
    vimeoPlayer.on('error', (error) => {
      if (onError) onError(error);
    });
    vimeoPlayer.on('progress', (data) => {
      if (onProgress) onProgress(data);
    });
    vimeoPlayer.on('durationchange', (durationObject) => {
      if (onDuration) onDuration(durationObject.duration);
    });
    vimeoPlayer.on('timeupdate', (data) => {
      if (onProgress) {
        // Map Vimeo progress data to match ReactPlayer's played/playedSeconds structure
        const played = data.percent; // Fraction of video completed (0 to 1)
        const playedSeconds = data.seconds; // Seconds into the video

        onProgress({ played, playedSeconds });
      }
    });
    

    // Set the player instance
    setPlayer(vimeoPlayer);

    // Cleanup player when component unmounts
    return () => {
      if (vimeoPlayer) {
        vimeoPlayer.destroy();
      }
    };
  }, [url, playing, controls, pip, loop, playbackRate, volume, muted, playerWidth, playerHeight]);

  return (
    <div
    ref={internalRef}
    ></div>
  );
});

export default VimeoPlayer;
